/**
 * The World owns all simulation state and entity lifecycle (spawn, respawn,
 * bot population). It is pure: no DOM, no canvas, no network, no globals — just
 * data + a seeded RNG + the injected config and action set. This is the unit
 * that can be lifted onto a server unchanged.
 *
 * The actual per-tick update lives in ./simulation.ts (step()), which operates
 * on a World. Keeping state (World) and behaviour (step) separable keeps both
 * easy to test.
 */
import type { GameConfig } from '../config/config';
import type { Action } from './actions';
import { Rng } from './math/rng';
import { clone } from './math/vec2';
import { SpatialHash } from './spatialHash';
import { emptyIntent } from './types';
import type { Entity, EntityId } from './types';

/**
 * Bot-naming inputs, injected so the core stays decoupled from the feature flag
 * and the specific name list (both live in src/config). `names` is plain data;
 * `enabled` mirrors FEATURES.botNames.
 */
export interface BotNaming {
  names: readonly string[];
  enabled: boolean;
}

export class World {
  readonly cfg: GameConfig;
  readonly rng: Rng;
  readonly actions: Action[];
  readonly spatial: SpatialHash;

  entities: Entity[] = [];
  readonly byId = new Map<EntityId, Entity>();
  playerId: EntityId | null = null;
  time = 0;

  private nextId = 1;
  private readonly botNames: readonly string[];
  private readonly botNamesEnabled: boolean;

  constructor(
    cfg: GameConfig,
    seed: number,
    actions: Action[],
    naming: BotNaming = { names: [], enabled: false },
  ) {
    this.cfg = cfg;
    this.rng = new Rng(seed);
    this.actions = actions;
    this.spatial = new SpatialHash(cfg.spatial.cellSize);
    this.botNames = naming.names;
    this.botNamesEnabled = naming.enabled;
  }

  get player(): Entity | null {
    return this.playerId != null ? (this.byId.get(this.playerId) ?? null) : null;
  }

  private allocId(): EntityId {
    return this.nextId++;
  }

  /** Pick a spawn position, biased away from blobs large enough to eat `size`.
   *  Uses the surface (edge-to-edge) gap so large predators are properly avoided
   *  even at the raised size cap; clearance target is config-driven. */
  private safeSpawnPos(size: number): { x: number; y: number } {
    const { width, height, spawnClearance } = this.cfg.world;
    const margin = size + 20;
    let best = { x: width / 2, y: height / 2 };
    let bestGap = -Infinity;
    for (let attempt = 0; attempt < 8; attempt++) {
      const x = this.rng.range(margin, width - margin);
      const y = this.rng.range(margin, height - margin);
      let minGap = Infinity;
      for (const e of this.entities) {
        if (!e.alive) continue;
        if (e.size >= size * this.cfg.eat.sizeRatio) {
          const gap = Math.hypot(e.pos.x - x, e.pos.y - y) - e.size - size;
          if (gap < minGap) minGap = gap;
        }
      }
      if (minGap > bestGap) {
        bestGap = minGap;
        best = { x, y };
      }
      // Good enough — comfortably clear of every predator's edge.
      if (minGap > spawnClearance) break;
    }
    return best;
  }

  private makeEntity(kind: Entity['kind'], size: number, name: string, skinId: string): Entity {
    const pos = this.safeSpawnPos(size);
    const e: Entity = {
      id: this.allocId(),
      kind,
      name,
      pos,
      vel: { x: 0, y: 0 },
      prevPos: clone(pos),
      size,
      alive: true,
      skinId,
      rateTokens: {},
      boostTimer: 0,
      peakSize: size,
      massEaten: 0,
      kills: 0,
      bornAt: this.time,
      intent: emptyIntent(),
    };
    this.entities.push(e);
    this.byId.set(e.id, e);
    return e;
  }

  spawnPlayer(name: string, skinId: string): Entity {
    const e = this.makeEntity('player', this.cfg.world.startSize, name || 'Player', skinId);
    this.playerId = e.id;
    return e;
  }

  /** Reset the existing player back to start state (used on respawn). */
  respawnPlayer(name: string, skinId: string): Entity {
    if (this.playerId != null) this.removeEntity(this.playerId);
    return this.spawnPlayer(name, skinId);
  }

  /**
   * Pick a bot name via the SEEDED rng (so assignments are reproducible): never
   * the player's name and never a name already on a live blob — both
   * case-insensitive. A freed name (its bot died) becomes reusable. Returns ''
   * when bot names are disabled. Scanning from a random start keeps variety
   * while staying deterministic; the numeric fallback only triggers in the
   * impossible case of more live bots than available names.
   */
  private pickBotName(): string {
    const names = this.botNames;
    if (!this.botNamesEnabled || names.length === 0) return '';
    const taken = new Set<string>();
    for (const e of this.entities) if (e.alive) taken.add(e.name.toLowerCase());
    const player = this.player;
    if (player) taken.add(player.name.toLowerCase());

    const start = this.rng.int(0, names.length - 1);
    for (let i = 0; i < names.length; i++) {
      const candidate = names[(start + i) % names.length];
      if (!taken.has(candidate.toLowerCase())) return candidate;
    }
    return `${names[start]}${this.rng.int(2, 999)}`;
  }

  spawnBot(): Entity {
    const b = this.cfg.bots;
    const size = this.rng.range(b.minSpawnSize, b.maxSpawnSize);
    const name = this.pickBotName();
    const skinId = 'default';
    const e = this.makeEntity('bot', size, name, skinId);
    e.ai = {
      mode: 'wander',
      nextThinkTime: 0,
      nextWanderTime: 0,
      heading: this.rng.direction(),
      targetId: null,
    };
    return e;
  }

  /** Top up the bot population to the configured count. */
  maintainBotPopulation(): void {
    let botCount = 0;
    for (const e of this.entities) if (e.alive && e.kind === 'bot') botCount++;
    while (botCount < this.cfg.bots.count) {
      this.spawnBot();
      botCount++;
    }
  }

  removeEntity(id: EntityId): void {
    this.byId.delete(id);
    const idx = this.entities.findIndex((e) => e.id === id);
    if (idx >= 0) this.entities.splice(idx, 1);
    if (this.playerId === id) this.playerId = null;
  }

  /** Drop dead non-player entities from the arrays (called after eating). */
  reapDead(): void {
    if (this.entities.some((e) => !e.alive && e.kind !== 'player')) {
      this.entities = this.entities.filter((e) => {
        const keep = e.alive || e.kind === 'player';
        if (!keep) this.byId.delete(e.id);
        return keep;
      });
    }
  }

  /** Live ranking of all entities by raw size. (The HUD leaderboard ranks by
   *  score via entityScore(); this remains as a physical-size helper.) */
  ranking(): Entity[] {
    return this.entities.filter((e) => e.alive).sort((a, b) => b.size - a.size);
  }
}
